import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Paper,
  CircularProgress,
  GlobalStyles,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useDownloadAdminCommissionReport } from '../../Query/ReportsQuery';
import {
  AdminCommissionReportRequest,
  AdminCommissionReportRequestDateRangeEnum,
} from '../../../client-sdk/api';

type AdminCommissionReportProps = object;

const AdminCommissionReport: React.FC<AdminCommissionReportProps> = () => {
  const { t } = useTranslation();
  const { mutate: downloadReport, isPending: isDownloading } = useDownloadAdminCommissionReport();

  // Get logged-in user's merchant ID
  const loggedInMerchantId = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData?.userId as string | undefined;
  }, []);

  // Filter states
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs().endOf('month'));
  const [selectedStores, setSelectedStores] = useState<string[]>([]);

  // Date validation
  const isDateToAfterToday = useMemo(
    () => (dateTo ? dateTo.isAfter(dayjs().endOf('day')) : false),
    [dateTo]
  );

  const isDateToBeforeDateFrom = useMemo(
    () => (dateFrom && dateTo ? dateTo.isBefore(dateFrom.startOf('day')) : false),
    [dateFrom, dateTo]
  );

  const dateToError = isDateToAfterToday || isDateToBeforeDateFrom;
  const dateToHelperText = isDateToAfterToday
    ? t('reports.dateToAfterToday') || 'Date To cannot be greater than today'
    : isDateToBeforeDateFrom
    ? t('reports.dateToBeforeDateFrom') || 'Date To cannot be before Date From'
    : undefined;

  // Fetch stores
  const storeQueryParams = useMemo(
    () => ({
      sortBy: 'createdAt',
      sortOrder: 'asc' as const,
      page: 1,
      limit: 100,
      addedByFilter: 'All',
      statusFilter: 'All',
      search: undefined,
    }),
    []
  );
  const { data: storesData } = useAllStores(storeQueryParams);

  // Store options
  const storeOptions = useMemo(
    () =>
      (storesData?.data ?? []).map(
        (store: { id: string; nameEnglish?: string; restaurantDetails?: { storeName?: string } }) => ({
          value: store.id,
          label: store.nameEnglish || store.restaurantDetails?.storeName || '',
        })
      ),
    [storesData]
  );

  const handleDownload = () => {
    // Validate dates before proceeding
    if (dateToError) {
      return;
    }

    const from = dateFrom ?? dayjs();
    const to = dateTo ?? dateFrom ?? dayjs();

    // Build request
    const request: AdminCommissionReportRequest = {
      dateRange: AdminCommissionReportRequestDateRangeEnum.Custom,
      dateFrom: from.startOf('day').toISOString(),
      dateTo: to.endOf('day').toISOString(),
      merchantId: loggedInMerchantId,
      storeIds: selectedStores.length > 0 ? selectedStores : undefined,
    };

    downloadReport(request);
  };

  // Common input styling
  const commonInputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '40px !important',
      minHeight: '40px !important',
      '& .MuiSelect-select': {
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,
        lineHeight: '40px',
      },
      '& .MuiAutocomplete-input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
      '& .MuiInputBase-input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
      '& input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <GlobalStyles
        styles={{
          '.MuiAutocomplete-popper': {
            zIndex: '1300 !important',
          },
        }}
      />
      <Box sx={{ 
        py: 3,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            mb={3} 
            sx={{ 
              color: 'text.primary',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            {t('reports.adminCommission.title')}
          </Typography>

          <Paper 
            elevation={1}
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 3,
            }}
          >
            <Grid container spacing={3}>
                  {/* Date From */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {t('reports.dateFrom')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <DatePicker
                          value={dateFrom}
                          onChange={(newValue) => setDateFrom(newValue)}
                          maxDate={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: dateToError,
                              helperText: dateToError ? dateToHelperText : undefined,
                              sx: commonInputSx,
                              placeholder: t('reports.dateFrom'),
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Date To */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {t('reports.dateTo')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <DatePicker
                          value={dateTo}
                          onChange={(newValue) => setDateTo(newValue)}
                          maxDate={dayjs()}
                          minDate={dateFrom ? dateFrom : undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: dateToError,
                              helperText: dateToError ? dateToHelperText : undefined,
                              sx: commonInputSx,
                              placeholder: t('reports.dateTo'),
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Stores */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={3}>
                        <Typography sx={{ fontWeight: 500, pt: 1 }}>
                          {t('reports.stores')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <Autocomplete
                          multiple
                          options={storeOptions}
                          getOptionLabel={(option) => option.label}
                          value={storeOptions.filter((opt) =>
                            selectedStores.includes(opt.value)
                          )}
                          onChange={(_, newValue) => {
                            setSelectedStores(newValue.map((v) => v.value));
                          }}
                          disableCloseOnSelect
                          openOnFocus
                          clearOnBlur={false}
                          disableClearable
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={t('reports.selectStores')}
                              variant="outlined"
                              sx={commonInputSx}
                            />
                          )}
                          renderTags={() => null}
                          sx={{ width: '100%' }}
                        />
                        {selectedStores.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              mt: 1.5,
                            }}
                          >
                            {storeOptions
                              .filter((opt) => selectedStores.includes(opt.value))
                              .map((option) => (
                                <Chip
                                  key={option.value}
                                  label={option.label}
                                  onDelete={() => {
                                    setSelectedStores((prev) =>
                                      prev.filter((val) => val !== option.value)
                                    );
                                  }}
                                  size="small"
                                />
                              ))}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Download Button */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Button
                        variant="contained"
                        onClick={handleDownload}
                        disabled={isDownloading || dateToError}
                        size="large"
                        sx={{ 
                          minWidth: 200,
                          height: '40px',
                        }}
                      >
                        {isDownloading ? (
                          <>
                            <CircularProgress size={24} sx={{ mr: 1.5, color: 'white' }} />
                            {t('reports.downloading')}
                          </>
                        ) : (
                          t('reports.download')
                        )}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminCommissionReport;

